import type { ManifestMedia } from '@tbcast/shared';
import { t } from '../i18n';
import { createPresentationRenderable } from './presentation';
import { createWidgetRenderable } from '../widgets';
import type { PlaybackContext, Renderable, SequenceItem } from './types';
import { withTimeout } from './types';

function layer(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'tb-layer';
  return el;
}

/** Releases a media element's decoder and network connection. */
export function releaseMedia(m: HTMLMediaElement): void {
  try {
    m.pause();
    m.removeAttribute('src');
    while (m.firstChild) m.removeChild(m.firstChild);
    m.load();
  } catch {
    // ignore
  }
}

function imageRenderable(urls: string[], fit: 'contain' | 'cover' = 'contain'): Renderable {
  const el = layer();
  const img = document.createElement('img');
  img.className = `tb-fill tb-fit-${fit}`;
  img.alt = '';
  img.decoding = 'async';
  el.appendChild(img);
  let i = 0;
  const load = (): Promise<void> =>
    new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => {
        i++;
        if (i < urls.length) {
          img.src = urls[i]!;
        } else reject(new Error('image failed'));
      };
      img.src = urls[0]!;
    });
  let destroyed = false;
  return {
    el,
    async ready() {
      await load();
      await img.decode?.().catch(() => undefined);
    },
    play() {},
    destroy() {
      if (destroyed) return;
      destroyed = true;
      img.onload = null;
      img.onerror = null;
      img.removeAttribute('src');
      el.remove();
    },
  };
}

function videoRenderable(
  urls: string[],
  opts: { loop: boolean; volume: number; poster?: string | null },
): Renderable {
  const el = layer();
  const video = document.createElement('video');
  video.className = 'tb-fill tb-fit-contain';
  video.muted = opts.volume <= 0;
  video.volume = Math.min(1, Math.max(0, opts.volume / 100));
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.preload = 'auto';
  video.loop = opts.loop;
  video.disablePictureInPicture = true;
  if (opts.poster) video.poster = opts.poster;
  el.appendChild(video);
  let idx = 0;
  let endedCb: (() => void) | null = null;
  let destroyed = false;
  const onEnded = () => endedCb?.();
  video.addEventListener('ended', onEnded);

  const tryLoad = (): Promise<void> =>
    new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        idx++;
        if (idx < urls.length) tryLoad().then(resolve, reject);
        else reject(new Error('video failed'));
      };
      const cleanup = () => {
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('error', onError);
      };
      video.addEventListener('canplay', onReady, { once: true });
      video.addEventListener('error', onError, { once: true });
      video.src = urls[idx]!;
      video.load();
    });

  return {
    el,
    async ready() {
      await tryLoad();
    },
    get intrinsicDurationMs() {
      return Number.isFinite(video.duration) && !opts.loop
        ? Math.round(video.duration * 1000)
        : undefined;
    },
    play() {
      video.currentTime = 0;
      video.play().catch(() => {
        // Autoplay with sound blocked: retry muted.
        video.muted = true;
        void video.play().catch(() => undefined);
      });
    },
    onEnded(cb) {
      endedCb = cb;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      video.removeEventListener('ended', onEnded);
      releaseMedia(video);
      el.remove();
    },
  };
}

function audioRenderable(url: string, name: string, volume: number): Renderable {
  const el = layer();
  el.classList.add('tb-audio');
  el.innerHTML = `<div class="tb-audio-card"><div class="tb-audio-bars"><span></span><span></span><span></span><span></span></div><p></p></div>`;
  el.querySelector('p')!.textContent = name;
  const audio = new Audio();
  audio.preload = 'auto';
  audio.volume = Math.min(1, Math.max(0, (volume || 60) / 100));
  let endedCb: (() => void) | null = null;
  audio.addEventListener('ended', () => endedCb?.());
  return {
    el,
    ready: () =>
      new Promise((resolve, reject) => {
        audio.addEventListener('canplay', () => resolve(), { once: true });
        audio.addEventListener('error', () => reject(new Error('audio failed')), { once: true });
        audio.src = url;
        audio.load();
      }),
    get intrinsicDurationMs() {
      return Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : undefined;
    },
    play() {
      void audio.play().catch(() => undefined);
    },
    onEnded(cb) {
      endedCb = cb;
    },
    destroy() {
      releaseMedia(audio);
      el.remove();
    },
  };
}

function webRenderable(
  url: string,
  ctx: PlaybackContext,
  opts: { zoom?: number; refreshSec?: number } = {},
): Renderable {
  const el = layer();
  el.classList.add('tb-web');
  let iframe: HTMLIFrameElement | null = null;
  let refresh: ReturnType<typeof setInterval> | null = null;
  return {
    el,
    async ready() {
      const embeddable = navigator.onLine ? await ctx.api.frameCheck(url) : true;
      if (!embeddable) {
        el.innerHTML = `<div class="tb-notice"><strong></strong><span></span></div>`;
        el.querySelector('strong')!.textContent = t(ctx.locale, 'webBlockedTitle');
        el.querySelector('span')!.textContent = new URL(url).hostname;
        return;
      }
      iframe = document.createElement('iframe');
      iframe.className = 'tb-fill';
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-forms');
      iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');
      iframe.referrerPolicy = 'no-referrer';
      iframe.loading = 'eager';
      const zoom = opts.zoom ?? 1;
      if (zoom !== 1) {
        iframe.style.width = `${100 / zoom}%`;
        iframe.style.height = `${100 / zoom}%`;
        iframe.style.transform = `scale(${zoom})`;
        iframe.style.transformOrigin = '0 0';
      }
      el.appendChild(iframe);
      await withTimeout(
        new Promise<void>((resolve) => {
          iframe!.addEventListener('load', () => resolve(), { once: true });
          iframe!.src = url;
        }),
        15_000,
        undefined,
      );
    },
    play() {
      if (opts.refreshSec && opts.refreshSec > 0 && iframe) {
        refresh = setInterval(() => {
          if (iframe) iframe.src = url;
        }, opts.refreshSec * 1000);
      }
    },
    destroy() {
      if (refresh) clearInterval(refresh);
      if (iframe) {
        iframe.src = 'about:blank';
        iframe.remove();
      }
      el.remove();
    },
  };
}

export { webRenderable };

/** Animated GIF: plays the MP4 conversion, falls back to the original GIF image. */
function gifRenderable(mp4Url: string, gifUrl: string | null): Renderable {
  const wrapper = layer();
  let inner: Renderable = videoRenderable([mp4Url], { loop: true, volume: 0 });
  wrapper.appendChild(inner.el);
  return {
    el: wrapper,
    async ready() {
      try {
        await inner.ready();
      } catch (err) {
        inner.destroy();
        if (!gifUrl) throw err;
        inner = imageRenderable([gifUrl]);
        wrapper.appendChild(inner.el);
        await inner.ready();
      }
    },
    play() {
      inner.play();
    },
    destroy() {
      inner.destroy();
      wrapper.remove();
    },
  };
}

function mediaUrls(m: ManifestMedia, ctx: PlaybackContext): string[] {
  const ids = [m.assetId, ...(m.fallbackAssetIds ?? [])].filter((x): x is string => !!x);
  return ids.map((id) => ctx.cache.url(id)).filter((u): u is string => !!u);
}

/** Builds the renderable for one sequence item. Returns null when the content is unavailable. */
export function createRenderable(
  item: SequenceItem,
  ctx: PlaybackContext,
  opts: { loopMedia?: boolean } = {},
): Renderable | null {
  const m = ctx.manifest;
  if (item.kind === 'presentation') {
    const p = m.presentations.find((x) => x.id === item.refId);
    return p ? createPresentationRenderable(p.doc, ctx, { loop: !!opts.loopMedia }) : null;
  }
  if (item.kind === 'widget') {
    const w = m.widgets.find((x) => x.id === item.refId);
    return w ? createWidgetRenderable(w.type, w.config, ctx) : null;
  }
  const media = m.media.find((x) => x.id === item.refId);
  if (!media) return null;
  switch (media.type) {
    case 'image': {
      const urls = mediaUrls(media, ctx);
      return urls.length ? imageRenderable(urls) : null;
    }
    case 'gif':
    case 'video': {
      const urls = mediaUrls(media, ctx);
      if (!urls.length) return null;
      // A GIF's fallback is the original GIF image, not a video.
      if (media.type === 'gif') return gifRenderable(urls[0]!, urls[1] ?? null);
      const poster = ctx.cache.url(`${media.id}:poster`);
      return videoRenderable(urls, { loop: !!opts.loopMedia, volume: ctx.volume, poster });
    }
    case 'audio': {
      const url = mediaUrls(media, ctx)[0];
      return url ? audioRenderable(url, media.name, ctx.volume) : null;
    }
    case 'pdf':
    case 'pptx': {
      const pageId = media.pageAssetIds?.[item.page ?? 0];
      const url = pageId ? ctx.cache.url(pageId) : null;
      return url ? imageRenderable([url]) : null;
    }
    case 'url':
    case 'html':
      return media.url ? webRenderable(media.url, ctx) : null;
  }
}
